import React, { useEffect, useMemo, useState } from 'react';
import { BACKEND_URL } from '../../config';
import { V4Calendar } from './V4Calendar';
import { V4Table } from './V4Table';
import { rateColor } from './v4Format';

const tally = (rows) => {
  const wins = rows.filter((r) => r.result === 'win').length;
  const losses = rows.filter((r) => r.result === 'miss').length;
  const fin = wins + losses;
  return { total: rows.length, wins, losses, pending: rows.length - fin, win_rate: fin ? Math.round((wins / fin) * 1000) / 10 : 0, finalized: fin };
};

const StatBox = ({ label, value, color, testId }) => (
  <div data-testid={testId} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: '0.6rem 0.9rem', minWidth: 96 }}>
    <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: '1.35rem', fontWeight: 800, color }}>{value}</div>
  </div>
);

// 👤 User 💀 V4 folder: calendar + win rate + the V4 trade table (no Scanner column).
export default function UserV4Folder() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [month, setMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [day, setDay] = useState(null);

  const load = async () => {
    setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/v4-folder`, { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } });
      const d = await res.json();
      if (d.success) setData(d); else setError(d.error || d.detail || 'Could not load the V4 folder');
    } catch (e) { setError('Could not connect to the server'); }
  };
  useEffect(() => { load(); }, []);

  const fires = useMemo(() => (data?.fires || []).filter((f) => !day || f.day === day), [data, day]);
  const s = useMemo(() => tally(fires), [fires]);

  return (
    <div data-testid="user-v4-folder">
      <h2 style={{ fontSize: '1.5rem', marginBottom: '0.35rem', color: '#1e293b' }}>V4 Folder</h2>
      <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '1rem' }}>
        Every 💀 V4 trade and how it turned out. 💀 marks trades that fired in bad market conditions. Perfect Detection / Wrong Dir counts as a loss.
      </p>
      {error && (
        <div data-testid="user-v4-error" style={{ color: '#dc2626', marginBottom: '1rem', fontWeight: 600 }}>
          {error} <button data-testid="user-v4-retry" onClick={load} style={{ marginLeft: 8, padding: '0.3rem 0.8rem', borderRadius: 8, border: '1px solid #cbd5e1', background: 'white', cursor: 'pointer' }}>↻ Retry</button>
        </div>
      )}
      {!data && !error && <div data-testid="user-v4-loading" style={{ color: '#64748b', padding: '1rem 0' }}>⏳ Loading the V4 folder…</div>}
      {data && (
        <>
          <V4Calendar days={data.days || {}} month={month} setMonth={setMonth} selectedDay={day} onSelect={setDay} />
          {day && (
            <div data-testid="user-v4-day-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '0.6rem 1rem', marginBottom: '0.9rem' }}>
              <div style={{ fontWeight: 800, color: '#1e293b' }}>📅 {new Date(`${day}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
              <button data-testid="user-v4-day-clear" onClick={() => setDay(null)} style={{ border: '1px solid #cbd5e1', background: 'white', color: '#334155', borderRadius: 8, padding: '6px 12px', fontWeight: 700, cursor: 'pointer' }}>✕ Show all days</button>
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '1rem' }}>
            <StatBox testId="user-v4-winrate" label="Win rate" value={`${s.win_rate}%`} color={s.finalized ? rateColor(s.win_rate) : '#94a3b8'} />
            <StatBox testId="user-v4-wins" label="Wins" value={s.wins} color="#16a34a" />
            <StatBox testId="user-v4-losses" label="Losses" value={s.losses} color="#dc2626" />
            <StatBox testId="user-v4-pending" label="Still running" value={s.pending} color="#64748b" />
            <StatBox testId="user-v4-total" label="Trades" value={s.total} color="#1e293b" />
          </div>
          {fires.length === 0
            ? <div data-testid="user-v4-empty" style={{ textAlign: 'center', padding: '2.5rem', background: '#f8fafc', borderRadius: 12, border: '2px dashed #e2e8f0', color: '#64748b' }}>No 💀 V4 trades yet. New trades appear here as they fire.</div>
            : <V4Table fires={fires} />}
        </>
      )}
    </div>
  );
}
